// AdminUserManagement.js v2.0 - Uses Portal Backend with redirect handling
// Create users, assign roles, manage portal access, send invites
import React, { useState, useEffect } from 'react';
import { 
// eslint-disable-next-line no-unused-vars
  Users, UserPlus, Search, Filter, Edit2, Trash2, Mail, 
// eslint-disable-next-line no-unused-vars
  Shield, CheckCircle, XCircle, Clock, Eye, EyeOff,
// eslint-disable-next-line no-unused-vars
  Key, Send, AlertTriangle, ChevronDown, X, RefreshCw,
  Building, Briefcase, HardHat, Crown
} from 'lucide-react';
import { URLS } from '../config/constants';

// Portal backend (handles login, createUser, setPassword, resetPassword)
const PORTAL_URL = URLS.portalScript;
const GATEWAY_URL = PORTAL_URL;
const EMAIL_GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbwv_5HDf9EhFPi0HhW0zqah5TzR1G3o4Hqo8ytdnq-G2xMuIl9_CbHPVcWCU2T2pgvK/exec';
const SHEETS_GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const EMAIL_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const USERS_SHEET_ID = '1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw';
const USERS_CSV_URL = 'https://docs.google.com/spreadsheets/d/1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw/gviz/tq?tqx=out:csv';

// Helper: POST to GAS with redirect following (GAS returns 302 → follow to get JSON)
const callPortal = async (payload) => {
  const response = await fetch(PORTAL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(payload)
  });
  const text = await response.text();
  // GAS returns HTML with redirect URL
  if (text.includes('HREF="')) {
    const match = text.match(/HREF="([^"]+)"/i);
    if (match) {
      const redirectUrl = match[1].replace(/&amp;/g, '&');
      const finalResponse = await fetch(redirectUrl);
      const finalText = await finalResponse.text();
      return JSON.parse(finalText);
    }
  }
  // Direct JSON response
  return JSON.parse(text);
};

// Helper: Parse CSV text into array of objects
const parseCSVText = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];
  const parseRow = (row) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') { inQuotes = !inQuotes; }
      else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else { current += char; }
    }
    values.push(current.trim());
    return values;
  };
  const headers = parseRow(lines[0]);
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseRow(lines[i]);
    const obj = {};
    headers.forEach((header, idx) => { obj[header] = values[idx] || ''; });
    obj.id = i;
    data.push(obj);
  }
  return data;
};

function AdminUserManagement({ darkMode, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Theme colors
  const accentPrimary = darkMode ? '#c850c0' : '#0077B6';
// eslint-disable-next-line no-unused-vars
  const accentSecondary = darkMode ? '#ff6b35' : '#00b4d8';
  const bgColor = darkMode ? '#0d1b2a' : '#f8fafc';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  // Role configurations
  const roles = [
    { id: 'admin', label: 'Admin', icon: Crown, color: '#ef4444', description: 'Full system access' },
    { id: 'supervisor', label: 'Supervisor', icon: Shield, color: '#8b5cf6', description: 'Approve work, manage crews' },
    { id: 'employee', label: 'Employee', icon: HardHat, color: '#3b82f6', description: 'Field worker access' },
    { id: 'contractor', label: 'Contractor', icon: Briefcase, color: '#10b981', description: 'Contractor company access' },
    { id: 'sub', label: 'Sub', icon: HardHat, color: '#f59e0b', description: 'Sub-contractor under a contractor' }
  ];

  const getRoleConfig = (roleId) => roles.find(r => r.id === roleId) || roles[2];

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(USERS_CSV_URL + '&_t=' + Date.now());
      const csvText = await response.text();
      const userList = parseCSVText(csvText)
        .filter(u => u.email && u.email.trim()) // skip cleared/deleted rows
        .map(u => {
          // eslint-disable-next-line no-unused-vars
          const { passwordHash, tempPassword, ...safeUser } = u;
          return safeUser;
        });
      setUsers(userList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    }
    setLoading(false);
  };

  // No mock users - all data from database
  const getMockUsers = () => [];

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Create new user
  // Send branded welcome email via Gateway v2.1
  const sendBrandedInvite = async (email, name, role) => {
    const firstName = name.split(' ')[0];
    const roleName = role.charAt(0).toUpperCase() + role.slice(1);
    const setupUrl = 'https://lytcomm.com/#set-password?email=' + encodeURIComponent(email);
    const html = '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;"><tr><td align="center" style="padding:0;"><table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;">'
      + '<tr><td style="height:6px;background:linear-gradient(90deg,#0077B6 0%,#00b4d8 50%,#28a745 100%);"></td></tr>'
      + '<tr><td align="center" style="padding:36px 40px 24px;font-family:Arial,sans-serif;"><span style="font-size:38px;font-weight:800;letter-spacing:-2px;"><span style="color:#0a3a7d;">ly</span><span style="color:#00b4d8;">t</span></span><span style="font-size:22px;font-weight:400;color:#475569;vertical-align:top;margin-left:3px;"> Communications</span><br><span style="font-size:10px;letter-spacing:3.5px;text-transform:uppercase;color:#94a3b8;">Building Digital Futures</span></td></tr>'
      + '<tr><td style="padding:0 40px;"><div style="height:1px;background:linear-gradient(90deg,transparent,#e2e8f0,transparent);"></div></td></tr>'
      + '<tr><td style="padding:32px 40px 0;font-family:Arial,sans-serif;"><h1 style="font-size:26px;color:#0d1b2a;margin:0 0 8px;font-weight:700;">Welcome aboard, ' + firstName + '! </h1><p style="font-size:15px;color:#64748b;margin:0;line-height:1.5;">We\'re excited to have you on the team.</p></td></tr>'
      + '<tr><td style="padding:24px 40px;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f0f9ff;border-radius:12px;border:1px solid #bae6fd;"><tr><td style="padding:24px 28px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding-bottom:12px;"><span style="font-size:12px;text-transform:uppercase;letter-spacing:1.5px;color:#0077B6;font-weight:700;font-family:Arial,sans-serif;">Your Details</span></td></tr><tr><td style="padding-bottom:10px;font-family:Arial,sans-serif;"><span style="font-size:13px;color:#64748b;">Role</span><br><span style="font-size:15px;color:#0d1b2a;font-weight:600;">' + roleName + '</span></td></tr><tr><td style="padding-bottom:10px;font-family:Arial,sans-serif;"><span style="font-size:13px;color:#64748b;">Email</span><br><span style="font-size:15px;color:#0d1b2a;font-weight:600;">' + email + '</span></td></tr><tr><td style="font-family:Arial,sans-serif;"><span style="font-size:13px;color:#64748b;">Portal</span><br><span style="font-size:15px;color:#0077B6;font-weight:600;">lytcomm.com</span></td></tr></table></td></tr></table></td></tr>'
      + '<tr><td align="center" style="padding:8px 40px 12px;"><table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="border-radius:10px;background:linear-gradient(135deg,#0077B6 0%,#00b4d8 100%);"><a href="' + setupUrl + '" target="_blank" style="display:inline-block;padding:16px 48px;font-family:Arial,sans-serif;font-size:16px;font-weight:700;color:#ffffff;text-decoration:none;letter-spacing:0.3px;">Set Up Your Password &#8594;</a></td></tr></table></td></tr>'
      + '<tr><td align="center" style="padding:8px 40px 32px;font-family:Arial,sans-serif;"><p style="font-size:12px;color:#94a3b8;margin:0;">This link expires in 24 hours for security.</p></td></tr>'
      + '<tr><td style="padding:0 40px;"><div style="height:1px;background:linear-gradient(90deg,transparent,#e2e8f0,transparent);"></div></td></tr>'
      + '<tr><td style="padding:28px 40px;font-family:Arial,sans-serif;"><h2 style="font-size:16px;color:#0d1b2a;margin:0 0 16px;font-weight:700;">What\'s Next?</h2><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding-bottom:12px;font-family:Arial,sans-serif;"><table cellpadding="0" cellspacing="0" border="0"><tr><td valign="top" style="padding-right:12px;"><div style="width:28px;height:28px;border-radius:50%;background:#eff6ff;text-align:center;line-height:28px;font-size:13px;font-weight:700;color:#0077B6;">1</div></td><td style="font-size:14px;color:#475569;line-height:1.5;padding-top:3px;"><strong style="color:#0d1b2a;">Set your password</strong> using the button above</td></tr></table></td></tr><tr><td style="padding-bottom:12px;font-family:Arial,sans-serif;"><table cellpadding="0" cellspacing="0" border="0"><tr><td valign="top" style="padding-right:12px;"><div style="width:28px;height:28px;border-radius:50%;background:#eff6ff;text-align:center;line-height:28px;font-size:13px;font-weight:700;color:#0077B6;">2</div></td><td style="font-size:14px;color:#475569;line-height:1.5;padding-top:3px;"><strong style="color:#0d1b2a;">Log into the portal</strong> at lytcomm.com</td></tr></table></td></tr><tr><td style="font-family:Arial,sans-serif;"><table cellpadding="0" cellspacing="0" border="0"><tr><td valign="top" style="padding-right:12px;"><div style="width:28px;height:28px;border-radius:50%;background:#eff6ff;text-align:center;line-height:28px;font-size:13px;font-weight:700;color:#0077B6;">3</div></td><td style="font-size:14px;color:#475569;line-height:1.5;padding-top:3px;"><strong style="color:#0d1b2a;">Complete onboarding</strong> &mdash; your dashboard will guide you</td></tr></table></td></tr></table></td></tr>'
      + '<tr><td style="padding:0 40px;"><div style="height:1px;background:linear-gradient(90deg,transparent,#e2e8f0,transparent);"></div></td></tr>'
      + '<tr><td align="center" style="padding:24px 40px;font-family:Arial,sans-serif;"><p style="font-size:13px;color:#94a3b8;margin:0 0 4px;">Need help? Contact us anytime</p><p style="font-size:14px;color:#0077B6;margin:0;font-weight:600;">(888) 503-9460 &middot; info@lytcomm.com</p></td></tr>'
      + '<tr><td style="background:#f8fafc;padding:20px 40px;text-align:center;font-family:Arial,sans-serif;"><p style="font-size:11px;color:#94a3b8;margin:0 0 2px;">lyt Communications &middot; Professional Fiber Optic Construction</p><p style="font-size:11px;color:#94a3b8;margin:0;">12130 State Highway 3, Webster, TX 77598</p></td></tr>'
      + '<tr><td style="height:4px;background:linear-gradient(90deg,#0077B6 0%,#00b4d8 50%,#28a745 100%);"></td></tr>'
      + '</table></td></tr></table>';

    try {
      const resp = await fetch(EMAIL_GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          secret: EMAIL_SECRET,
          action: 'gmailSend',
          params: { to: email, subject: 'Welcome to lyt Communications — Let\u2019s Get You Set Up', htmlBody: html }
        })
      });
      const text = await resp.text();
      if (text.includes('HREF="')) {
        const match = text.match(/HREF="([^"]+)"/i);
        if (match) {
          const redirectUrl = match[1].replace(/&amp;/g, '&');
          await fetch(redirectUrl);
        }
      }
    } catch (e) {
      console.log('Branded email send attempted:', e);
    }
  };

  const handleCreateUser = async (userData) => {
    setActionLoading(true);
    try {
      const result = await callPortal({
        action: 'createUser',
        email: userData.email.toLowerCase(),
        name: userData.name,
        role: userData.role,
        company: userData.company || '',
        parent_email: userData.parent_email || '',
        invitedBy: currentUser?.email || 'admin'
      });

      if (result.success) {
        // Send branded welcome email
        await sendBrandedInvite(userData.email.toLowerCase(), userData.name, userData.role);
        showNotification(`User created! Branded invite sent to ${userData.email}`);
        fetchUsers();
        setShowAddModal(false);
      } else {
        showNotification(result.message || 'Failed to create user', 'error');
      }
    } catch (err) {
      console.error('Create user error:', err);
      showNotification('Error creating user. Please try again.', 'error');
    }
    setActionLoading(false);
  };

  // Update user
  const handleUpdateUser = async (userId, updates) => {
    setActionLoading(true);
    try {
      const user = users.find(u => u.id === userId);
      if (!user) throw new Error('User not found');
      
      const result = await callPortal({
        action: 'updateUser',
        email: user.email,
        ...updates
      });

      if (result.success) {
        showNotification('User updated successfully');
      } else {
        // Update locally even if backend fails
        showNotification('User updated (syncing...)');
      }
      fetchUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Update error:', err);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u));
      showNotification('User updated locally');
      setShowEditModal(false);
      setSelectedUser(null);
    }
    setActionLoading(false);
  };

  // Deactivate/Reactivate user
  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    await handleUpdateUser(user.id, { status: newStatus });
  };

  // Permanently delete user
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const handleDeleteUser = async (user) => {
    if (!deleteConfirm || deleteConfirm.id !== user.id) {
      setDeleteConfirm(user);
      return;
    }
    // Immediately remove from UI
    setUsers(prev => prev.filter(u => u.id !== user.id));
    setDeleteConfirm(null);
    showNotification(`${user.name || user.email} permanently removed`);

    // Clear from Google Sheet in background — find row by email, not index
    try {
      // Read all emails to find the actual sheet row for this user
      const findResp = await fetch(SHEETS_GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsRead',
          params: { spreadsheetId: USERS_SHEET_ID, range: 'Sheet1!A1:A100' }
        })
      });
      let findText = await findResp.text();
      let findResult;
      if (findText.includes('HREF="')) {
        const m = findText.match(/HREF="([^"]+)"/i);
        if (m) { const r = await fetch(m[1].replace(/&amp;/g, '&')); findResult = await r.json(); }
      } else {
        findResult = JSON.parse(findText);
      }

      if (findResult?.success) {
        const emails = findResult.data.data;
        // Find the row where email matches (emails[0] is header row, so sheet row = index + 1)
        const rowIndex = emails.findIndex((row, i) => i > 0 && row[0]?.toLowerCase() === user.email?.toLowerCase());
        if (rowIndex >= 0) {
          const sheetRow = rowIndex + 1;
          const range = `Sheet1!A${sheetRow}:I${sheetRow}`;
          const resp = await fetch(SHEETS_GATEWAY_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({
              secret: GATEWAY_SECRET,
              action: 'sheetsWrite',
              params: { spreadsheetId: USERS_SHEET_ID, range, values: [new Array(9).fill('')] }
            })
          });
          const t = await resp.text();
          if (t.includes('HREF="')) {
            const m2 = t.match(/HREF="([^"]+)"/i);
            if (m2) await fetch(m2[1].replace(/&amp;/g, '&'));
          }
        }
      }
    } catch (err) {
      console.error('Sheet cleanup error:', err);
    }
  };

  // Send password reset
  const handleResetPassword = async (user) => {
    setActionLoading(true);
    try {
      const result = await callPortal({
        action: 'resetPassword',
        email: user.email
      });
      if (result.success) {
        showNotification(`Password reset sent to ${user.email}`);
      } else {
        showNotification(result.message || 'Reset failed', 'error');
      }
    } catch (err) {
      console.error('Reset error:', err);
      showNotification('Error sending reset. Please try again.', 'error');
    }
    setActionLoading(false);
  };

  // Resend invite
  const handleResendInvite = async (user) => {
    setActionLoading(true);
    try {
      const result = await callPortal({
        action: 'createUser',
        email: user.email,
        name: user.name,
        role: user.role,
        company: user.company || '',
        invitedBy: currentUser?.email || 'admin'
      });
      if (result.success) {
        showNotification(`Invite resent to ${user.email}`);
      } else {
        // User might already exist - that's ok for resend
        showNotification(`Invite resent to ${user.email}`);
      }
    } catch (err) {
      console.error('Resend error:', err);
      showNotification(`Error resending invite`, 'error');
    }
    setActionLoading(false);
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    pending: users.filter(u => u.status === 'pending').length,
    admins: users.filter(u => u.role === 'admin').length,
    supervisors: users.filter(u => u.role === 'supervisor').length,
    employees: users.filter(u => u.role === 'employee').length,
    contractors: users.filter(u => u.role === 'contractor').length
  };

  return (
    <div style={{ padding: '24px', backgroundColor: bgColor, minHeight: '100vh' }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '16px 24px',
          borderRadius: '12px',
          backgroundColor: notification.type === 'error' ? '#fee2e2' : '#d1fae5',
          color: notification.type === 'error' ? '#dc2626' : '#059669',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideIn 0.3s ease'
        }}>
          {notification.type === 'error' ? <AlertTriangle size={20} /> : <CheckCircle size={20} />}
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: textColor, marginBottom: '4px' }}>
              User Management
            </h1>
            <p style={{ color: mutedColor }}>Manage portal access and user permissions <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>(v2.0)</span></p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: accentPrimary,
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.95rem',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <UserPlus size={20} /> Add User
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
        gap: '16px', 
        marginBottom: '24px' 
      }}>
        {[
          { label: 'Total Users', value: stats.total, icon: Users, color: accentPrimary },
          { label: 'Active', value: stats.active, icon: CheckCircle, color: '#10b981' },
          { label: 'Pending', value: stats.pending, icon: Clock, color: '#f59e0b' },
          { label: 'Admins', value: stats.admins, icon: Crown, color: '#ef4444' },
          { label: 'Supervisors', value: stats.supervisors, icon: Shield, color: '#8b5cf6' },
          { label: 'Employees', value: stats.employees, icon: HardHat, color: '#3b82f6' },
          { label: 'Contractors', value: stats.contractors, icon: Briefcase, color: '#10b981' }
        ].map((stat, idx) => (
          <div
            key={idx}
            style={{
              backgroundColor: cardBg,
              borderRadius: '12px',
              padding: '16px',
              border: `1px solid ${borderColor}`,
              textAlign: 'center'
            }}
          >
            <stat.icon size={24} color={stat.color} style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: textColor }}>{stat.value}</div>
            <div style={{ fontSize: '0.8rem', color: mutedColor }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={20} color={mutedColor} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search users by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 44px',
              borderRadius: '10px',
              border: `1px solid ${borderColor}`,
              backgroundColor: cardBg,
              color: textColor,
              fontSize: '0.95rem',
              outline: 'none'
            }}
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          style={{
            padding: '12px 16px',
            borderRadius: '10px',
            border: `1px solid ${borderColor}`,
            backgroundColor: cardBg,
            color: textColor,
            fontSize: '0.95rem',
            cursor: 'pointer',
            minWidth: '140px'
          }}
        >
          <option value="all">All Roles</option>
          {roles.map(role => (
            <option key={role.id} value={role.id}>{role.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '12px 16px',
            borderRadius: '10px',
            border: `1px solid ${borderColor}`,
            backgroundColor: cardBg,
            color: textColor,
            fontSize: '0.95rem',
            cursor: 'pointer',
            minWidth: '140px'
          }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          onClick={fetchUsers}
          style={{
            padding: '12px',
            borderRadius: '10px',
            border: `1px solid ${borderColor}`,
            backgroundColor: cardBg,
            color: textColor,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <RefreshCw size={20} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* Users Table */}
      <div style={{ 
        backgroundColor: cardBg, 
        borderRadius: '16px', 
        border: `1px solid ${borderColor}`,
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: mutedColor }}>
            <RefreshCw size={32} className="spin" style={{ marginBottom: '12px' }} />
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: mutedColor }}>
            <Users size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p style={{ fontWeight: '500' }}>No users found</p>
            <p style={{ fontSize: '0.9rem' }}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead>
                <tr style={{ backgroundColor: darkMode ? '#111827' : '#f8fafc' }}>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: '600', fontSize: '0.85rem', color: mutedColor }}>User</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: '600', fontSize: '0.85rem', color: mutedColor }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: '600', fontSize: '0.85rem', color: mutedColor }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: '600', fontSize: '0.85rem', color: mutedColor }}>Last Login</th>
                  <th style={{ textAlign: 'center', padding: '14px 20px', fontWeight: '600', fontSize: '0.85rem', color: mutedColor }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const roleConfig = getRoleConfig(user.role);
                  return (
                    <tr key={user.id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%', 
                            backgroundColor: `${roleConfig.color}20`,
                            color: roleConfig.color,
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontWeight: '600',
                            fontSize: '0.9rem'
                          }}>
                            {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                          </div>
                          <div>
                            <div style={{ fontWeight: '500', color: textColor }}>{user.name}</div>
                            <div style={{ fontSize: '0.85rem', color: mutedColor }}>{user.email}</div>
                            {user.company && (
                              <div style={{ fontSize: '0.8rem', color: mutedColor, display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                <Building size={12} /> {user.company}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: `${roleConfig.color}15`,
                          color: roleConfig.color,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <roleConfig.icon size={14} /> {roleConfig.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: user.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 
                                          user.status === 'pending' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                          color: user.status === 'active' ? '#10b981' : 
                                 user.status === 'pending' ? '#f59e0b' : '#6b7280',
                          textTransform: 'capitalize'
                        }}>
                          {user.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', color: mutedColor, fontSize: '0.9rem' }}>
                        {user.lastLogin || 'Never'}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => { setSelectedUser(user); setShowEditModal(true); }}
                            title="Edit User"
                            style={{
                              padding: '8px',
                              backgroundColor: `${accentPrimary}15`,
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              color: accentPrimary
                            }}
                          >
                            <Edit2 size={16} />
                          </button>
                          {user.status === 'pending' ? (
                            <button
                              onClick={() => handleResendInvite(user)}
                              title="Resend Invite"
                              style={{
                                padding: '8px',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                color: '#f59e0b'
                              }}
                            >
                              <Send size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleResetPassword(user)}
                              title="Reset Password"
                              style={{
                                padding: '8px',
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                color: '#3b82f6'
                              }}
                            >
                              <Key size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleStatus(user)}
                            title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                            style={{
                              padding: '8px',
                              backgroundColor: user.status === 'active' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              color: user.status === 'active' ? '#ef4444' : '#10b981'
                            }}
                          >
                            {user.status === 'active' ? <XCircle size={16} /> : <CheckCircle size={16} />}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            title={deleteConfirm?.id === user.id ? 'Click again to confirm delete' : 'Delete permanently'}
                            style={{
                              padding: '8px',
                              backgroundColor: deleteConfirm?.id === user.id ? '#ef4444' : 'rgba(239, 68, 68, 0.05)',
                              border: deleteConfirm?.id === user.id ? '2px solid #ef4444' : 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              color: deleteConfirm?.id === user.id ? '#fff' : '#ef444480',
                              transition: 'all 0.2s',
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        {deleteConfirm?.id === user.id && (
                          <div style={{ textAlign: 'center', paddingTop: 4 }}>
                            <span style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 600 }}>Click trash again to confirm</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          darkMode={darkMode}
          onClose={() => setShowAddModal(false)}
          onSave={handleCreateUser}
          loading={actionLoading}
          roles={roles}
          users={users}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          darkMode={darkMode}
          user={selectedUser}
          onClose={() => { setShowEditModal(false); setSelectedUser(null); }}
          onSave={(updates) => handleUpdateUser(selectedUser.id, updates)}
          loading={actionLoading}
          roles={roles}
        />
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

// Add User Modal Component
function AddUserModal({ darkMode, onClose, onSave, loading, roles, users }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'employee',
    company: '',
    phone: '',
    parent_email: '',
    sendInvite: true
  });

  const accentPrimary = darkMode ? '#c850c0' : '#0077B6';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${borderColor}`,
    backgroundColor: '#ffffff',
    color: '#1f2937',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: cardBg,
          borderRadius: '20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          padding: '24px', 
          borderBottom: `1px solid ${borderColor}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ color: textColor, margin: 0, fontSize: '1.25rem' }}>Add New User</h2>
            <p style={{ color: mutedColor, fontSize: '0.9rem', margin: '4px 0 0' }}>Create portal access for team members</p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: mutedColor
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Role Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: textColor, fontWeight: '500' }}>
              User Role *
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {roles.map(role => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, role: role.id })}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: `2px solid ${formData.role === role.id ? role.color : borderColor}`,
                    backgroundColor: formData.role === role.id ? `${role.color}10` : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <role.icon size={20} color={role.color} />
                    <span style={{ fontWeight: '600', color: textColor }}>{role.label}</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', color: mutedColor, margin: 0 }}>{role.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Smith"
              style={inputStyle}
              required
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              style={inputStyle}
              required
            />
          </div>

          {/* Phone */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 555-5555"
              style={inputStyle}
            />
          </div>

          {/* Company (for contractors and subs) */}
          {(formData.role === 'contractor' || formData.role === 'sub') && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
                Company Name *
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="ABC Construction LLC"
                style={inputStyle}
                required={formData.role === 'contractor' || formData.role === 'sub'}
              />
            </div>
          )}

          {/* Parent Contractor (for subs only) */}
          {formData.role === 'sub' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
                Parent Contractor *
              </label>
              <select
                value={formData.parent_email}
                onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                style={inputStyle}
                required={formData.role === 'sub'}
              >
                <option value="">Select parent contractor...</option>
                {(users || []).filter(u => u.role === 'contractor').map(c => (
                  <option key={c.email} value={c.email}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>
          )}

          {/* Send Invite Checkbox */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              cursor: 'pointer',
              color: textColor
            }}>
              <input
                type="checkbox"
                checked={formData.sendInvite}
                onChange={(e) => setFormData({ ...formData, sendInvite: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>Send invite email with password setup link</span>
            </label>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: accentPrimary,
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spin" /> Creating...
                </>
              ) : (
                <>
                  <UserPlus size={18} /> Create User
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '14px 24px',
                backgroundColor: 'transparent',
                color: textColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit User Modal Component
function EditUserModal({ darkMode, user, onClose, onSave, loading, roles }) {
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    role: user.role || 'employee',
    company: user.company || '',
    phone: user.phone || ''
  });

  const accentPrimary = darkMode ? '#c850c0' : '#0077B6';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${borderColor}`,
    backgroundColor: '#ffffff',
    color: '#1f2937',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: cardBg,
          borderRadius: '20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          padding: '24px', 
          borderBottom: `1px solid ${borderColor}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ color: textColor, margin: 0, fontSize: '1.25rem' }}>Edit User</h2>
            <p style={{ color: mutedColor, fontSize: '0.9rem', margin: '4px 0 0' }}>Update user details and permissions</p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: mutedColor
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Role Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: textColor, fontWeight: '500' }}>
              User Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              style={{
                ...inputStyle,
                cursor: 'pointer'
              }}
            >
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.label} - {role.description}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
              Full Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={inputStyle}
              required
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={inputStyle}
              required
            />
          </div>

          {/* Phone */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              style={inputStyle}
            />
          </div>

          {/* Company (for contractors) */}
          {formData.role === 'contractor' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: textColor, fontWeight: '500' }}>
                Company Name
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                style={inputStyle}
              />
            </div>
          )}

          {/* User Info */}
          <div style={{ 
            backgroundColor: darkMode ? '#111827' : '#f8fafc', 
            borderRadius: '10px', 
            padding: '16px', 
            marginBottom: '24px' 
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.85rem' }}>
              <div>
                <span style={{ color: mutedColor }}>Created:</span>
                <span style={{ color: textColor, marginLeft: '8px' }}>{user.createdAt}</span>
              </div>
              <div>
                <span style={{ color: mutedColor }}>Last Login:</span>
                <span style={{ color: textColor, marginLeft: '8px' }}>{user.lastLogin || 'Never'}</span>
              </div>
              <div>
                <span style={{ color: mutedColor }}>Status:</span>
                <span style={{ 
                  color: user.status === 'active' ? '#10b981' : '#f59e0b', 
                  marginLeft: '8px',
                  textTransform: 'capitalize'
                }}>{user.status}</span>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: accentPrimary,
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spin" /> Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={18} /> Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '14px 24px',
                backgroundColor: 'transparent',
                color: textColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminUserManagement;
